import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { TopBar } from '@/components/TopBar';
import { ChatConversationView } from '@/components/ChatPage/ChatConversationView';
import { ChatMessageCard } from '@/components/ChatPage/ChatMessageCard';
import { ChatErrorBanner } from '@/components/ChatPage/ChatErrorBanner';
import { WorkingBubble } from '@/components/ChatPage/WorkingBubble';
import {
  DisconnectBanner,
  NotSignedInBanner,
  PowerOnBanner,
  AgentErrorBanner,
  AgentTransitionBanner,
  AgentPoweredOffBanner,
} from '@/components/ChatPage/StatusBanners';
import { AnonSignInPrompt } from '@/components/AnonSignInPrompt';
import { DemoSignInPrompt } from '@/components/DemoSignInPrompt';
import { ChatInput, MobileChatIsland, useChatInput } from '@/components/ChatInput';
import { ImmediateResultSlot } from '@/components/ImmediateResult';
import { useAppSession } from '@/contexts/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { useSettings } from '@/hooks/useSettings';
import { useAgentStatus } from '@/hooks/useAgentStatus';
import { useMobileIsland } from '@/hooks/useMobileIsland';
import { useConversationHydration } from '@/hooks/useConversationHydration';
import { Titles } from '@/lib/navigation';
import { isSymbolicaUser } from '@/lib/utils';
import type { SubagentTimelineItem, TimelineItem } from '@/hooks/useChat';
import type { ActivityVisibility } from '@/hooks/useSettings';
import { usePaperThemeActive } from '@/hooks/useThemeFlag';
import { PaperConversationPage } from '@/components/paper/PaperConversationPage';

const shouldShowItem = (
  item: TimelineItem,
  visibility: ActivityVisibility,
  showSubagentCards: boolean,
): boolean => {
  if (item.kind === "subagent") {
    return visibility === "show_all" && showSubagentCards;
  }
  if (visibility === "show_all") return true;
  // `console` items only ever come from local-only `$$` dev commands —
  // they're never in stored history and the user just typed them, so
  // always render them regardless of activity-visibility setting.
  return (
    item.kind === "user" ||
    item.kind === "agent" ||
    item.kind === "system" ||
    item.kind === "download" ||
    item.kind === "auth_request" ||
    item.kind === "console"
  );
};

export const ConversationPage = () => {
  const isPaper = usePaperThemeActive();
  if (isPaper) return <PaperConversationPage />;
  return <ConversationPageLegacy />;
};

const ConversationPageLegacy = () => {
  const { id } = useParams<{ id: string }>();
  const { session, actions, artifacts, agentError, dismissAgentError } = useAppSession();
  const { chat, chatHistory, isPoweringOn } = session;
  const auth = useAuth();
  const isMobileDevice = useIsMobileDevice();
  const mobileIsland = useMobileIsland();
  const { settings, setActivityVisibility } = useSettings();

  const agentName = chatHistory.sessions.find((s) => s.id === id)?.title ?? 'Chat';
  useDocumentTitle(Titles.agent(agentName));

  const loading = useConversationHydration(id);

  const canViewAllActivity = isSymbolicaUser(auth.user?.email);
  const effectiveActivityVisibility = canViewAllActivity ? settings.activityVisibility : 'results_only';

  useEffect(() => {
    if (!canViewAllActivity && settings.activityVisibility !== 'results_only') {
      setActivityVisibility('results_only');
    }
  }, [canViewAllActivity, settings.activityVisibility, setActivityVisibility]);

  const { hasActiveRun, activeAgentStatus } = useAgentStatus(
    id ?? null,
    chat.activeChat?.isRunning,
    chatHistory.sessions,
  );

  const activeChat = chat.activeChat;
  const isRunning = (activeChat?.isRunning ?? false) || hasActiveRun;
  const runningSubagents = useMemo(
    () =>
      activeChat?.timeline.filter(
        (item): item is SubagentTimelineItem =>
          item.kind === "subagent" && item.status === "running",
      ) ?? [],
    [activeChat?.timeline],
  );

  const isExampleSession = chatHistory.sessions.find((s) => s.id === id)?.is_example ?? false;
  const requiresSignIn = !auth.isAuthenticated && isExampleSession;
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);

  const rawChatInput = useChatInput({
    isRunning,
    agentStatus: activeAgentStatus,
    sendInFlight: chat.sendInFlight,
    blocked: activeChat?.error?.type === 'payment-required',
  });
  const demoOnSubmit = useCallback((e: React.FormEvent) => { e.preventDefault(); setShowSignInPrompt(true); }, []);
  const chatInput = useMemo(
    () => requiresSignIn ? { ...rawChatInput, onSubmit: demoOnSubmit } : rawChatInput,
    [requiresSignIn, rawChatInput, demoOnSubmit],
  );

  // Show the "Working…" bubble while a run is in progress, unless the
  // agent is *actively* streaming text into its own bubble (in which
  // case the shimmer on that bubble already conveys progress) or a
  // pending auth request is blocking the run. Using the explicit
  // `isStreamingAgentText` flag rather than "last timeline item is
  // agent" ensures the bubble reappears while the agent generates /
  // executes a code block after an earlier text turn, and keeps it
  // visible when the agent starts a response with a code block.
  const showWorkingBubble =
    isRunning &&
    (!(activeChat?.isStreamingAgentText ?? false) || runningSubagents.length > 0) &&
    !activeChat?.timeline.some(
      (item) => item.kind === "auth_request" && item.status === "pending",
    );

  const visibleTimeline = useMemo(
    () =>
      activeChat?.timeline.filter((item) =>
        shouldShowItem(
          item,
          effectiveActivityVisibility,
          settings.showSubagentCards,
        ),
      ) ?? [],
    [activeChat?.timeline, effectiveActivityVisibility, settings.showSubagentCards],
  );

  const anonHasStartedSession = useMemo(
    () =>
      !auth.isAuthenticated &&
      !!activeChat &&
      activeChat.timeline.some((item) => item.kind === 'user'),
    [auth.isAuthenticated, activeChat],
  );

  if (loading || !activeChat) {
    return null;
  }

  return (
    <main
      className={cn(
        'relative flex flex-1 flex-col overflow-hidden transition-[padding] duration-300',
        artifacts.artifactOpen && 'desktop:pl-20',
      )}
    >
      {!auth.isAuthenticated && <TopBar />}

      <div className="flex min-h-0 flex-1 flex-col justify-start transition-all duration-300 ease-out">
        <ChatConversationView
          timeline={activeChat.timeline}
          workingIndicator={
            <WorkingBubble
              show={showWorkingBubble}
              status={activeChat?.workingStatus ?? ''}
              subagents={runningSubagents}
            />
          }
          errorBanner={<ChatErrorBanner error={activeChat.error} />}
          statusBanners={<>
            <DisconnectBanner show={chatHistory.disconnected} />
            <NotSignedInBanner
              show={isMobileDevice && !auth.isAuthenticated && anonHasStartedSession}
              onLogin={auth.signIn}
            />
            <PowerOnBanner show={isPoweringOn} />
            <AgentErrorBanner
              show={!!agentError}
              error={agentError}
              onDismiss={dismissAgentError}
            />
            <AgentTransitionBanner
              show={activeAgentStatus === "starting" || activeAgentStatus === "stopping"}
              agentStatus={activeAgentStatus}
            />
            <AgentPoweredOffBanner
              show={activeAgentStatus === "off"}
              onStartAgent={id ? () => actions.handleStartAgent(id) : undefined}
            />
          </>}
          chatInput={
            <>
              <ChatInput
                {...chatInput}
                isRunning={isRunning}
                mobileIsland={<MobileChatIsland config={mobileIsland} />}
                className="pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3"
              />
              <ImmediateResultSlot />
            </>
          }
        >
          {visibleTimeline.map((item) => (
            <ChatMessageCard
              key={item.id}
              item={item}
              sessionId={activeChat.sessionId}
              onAuthComplete={chat.markAuthComplete}
            />
          ))}
        </ChatConversationView>
      </div>

      {mobileIsland.anonPrompt?.visible && (
        <AnonSignInPrompt
          onSignIn={mobileIsland.anonPrompt.onSignIn}
          onContinue={mobileIsland.anonPrompt.onContinue}
          onCancel={mobileIsland.anonPrompt.onCancel}
        />
      )}

      {showSignInPrompt && (
        <DemoSignInPrompt
          onSignIn={() => { setShowSignInPrompt(false); auth.signIn(); }}
          onCancel={() => setShowSignInPrompt(false)}
        />
      )}
    </main>
  );
};
